import { RECS_MANAGEMENT_API, IDENTITY_API } from '@/api';
import { createMutations } from '@/utils/vuexHelper';
import RecsBaseStore from '../RecsBaseStore';
import dateStore from '@/utils/dateStore';
import moment from 'moment';

const state = {
  screenName: 'accountsScreen',
  // stands for 180 days in the past
  defaultDateDiff: 1095,
  hideZeros: true,

  selectedSystemNames: null,
  selectedEntityNames: [],
  selectedStatus: null,
  selectedDates: null,

  statusOptions: ['active', 'retired'],
  defaultStatus: 'active',

  certificateQtyData: [],
  certificateEligibilityData: [],
  accountData: [],
  scs: [],
  
  numSelectedRows: 0,
  numSelectedRecs: 0,
  selectedRows: [],

  certificateTableData: [],
  certificateConfig: {
    name: 'recAccountCertificates',
    columns: [
      {
        prop: 'account.name', label: 'Account', sortable: true, filterable: true, 
      },
      {
        prop: 'systemName', label: 'System Name', sortable: true, filterable: true,
      },
      {
        prop: 'account.shortId', label: 'Account Id', sortable: true, filterable: true,
      },
      {
        prop: 'account.accountType', label: 'Account Type', sortable: true, filterable: true,
      },
      {
        prop: 'account.status', label: 'Account Status', sortable: true, filterable: true,
      },
      {
        prop: 'certificateType', label: 'CertificateType', sortable: true, filterable: true,
      },
      {
        prop: 'vintageDate', label: 'VintageDate', sortable: true, filterable: true,
      },
      {
        prop: 'serialNumberBase', label: 'Serial# Base', sortable: true, filterable: true,
      },
      {
        prop: 'serialNumberStart', label: 'Serial# Start', sortable: true, filterable: true,
      },
      {
        prop: 'serialNumberEnd', label: 'Serial# End', sortable: true, filterable: true,
      },
      {
        prop: 'quantity', label: 'Quantity', sortable: true, filterable: true,
      },
      {
        prop: 'eligibility', label: 'Eligibility', sortable: true, filterable: true,
      },
      {
        prop: 'status', label: 'Certificate Status', sortable: true, filterable: true,
      },
      {
        prop: 'rrcQuantity', label: 'Rrc Qty', sortable: true, filterable: true,
      },
      {
        prop: 'hasEtagQuantities', label: 'Has Etag Qtys', sortable: true, filterable: true,
      },
      {
        prop: 'fuelType.name', label: 'Fuel Type', sortable: true, filterable: true,
      },
      {
        prop: 'generator.mretsId', label: 'GU ID', sortable: true, filterable: true,
      },
      {
        prop: 'generator.stateProvince', label: 'Generator Location', sortable: true, filterable: true,
      },
      {
        prop: 'generator.name', label: 'Generator', sortable: true, filterable: true,
      },
      {
        prop: 'generatorFuel.fuelSource.description', label: 'Fuel Source', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'organization.name', label: 'Organization', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'importSource', label: 'Import Source', sortable: true, filterable: true, visible: false,
      },
    ],
    groupSummary: [
      {
        prop: 'quantity',
        summaryType: 'custom',
        dataType: 'string',
        showInGroupFooter: false,
        showInColumn: 'account.name',
      },
    ],
    customSummary: sumData,
    options: {
      columnConfig: true,
      filterHeader: true,
      groupPanel: true,
      exportExcel: true,
      multipleSelection: true,
      allowSelectAll: true,
    },
  },
  ...RecsBaseStore.state,
};

function sumData(opt) {
  if (opt.summaryProcess === 'start') {
    opt.totalSum = 0;
    opt.totalCount = 0;
  } else if (opt.summaryProcess === 'calculate') {
    opt.totalSum += opt.value.quantity;
    opt.totalCount++;
  } else if (opt.summaryProcess === 'finalize') {
    opt.totalValue = `Count: ${ opt.totalCount }, Quantity Total: ${ opt.totalSum}`;
  }
}

const _getList = (options, key) => options.map((opt) => ({ value: opt[key], label: opt[key] }));

const getters = {
  scList: (state) => _getList(state.scs, 'name'),
  defaultEndDate: () => dateStore.getDefaultDateLocal(),
  currentSearchParams(state) {
    const currentStartDate = state.selectedDates ? dateStore.toDateFromLocal(state.selectedDates[0]) : null;
    const currentEndDate = state.selectedDates ? dateStore.toDateFromLocal(state.selectedDates[1]) : null;
    const currentDateDiff = currentEndDate.diff(currentStartDate, 'days');
    const defaultEndDate = getters.defaultEndDate();

    // send null dateRangeDiff so that it will use default on next page reload
    const dateRangeDiff = currentDateDiff != state.defaultDateDiff ? currentDateDiff : null;
    // send null endDate so that it will use default on next page reload
    const endDate = !moment(currentEndDate).isSame(defaultEndDate, 'day') ? currentEndDate : null;
    const searchParams = {
      systemName: state.selectedSystemNames,
      entityNames: state.selectedEntityNames,
      status: state.selectedStatus,
      dateRangeDiff,
      endDate,
    }
    return searchParams;
  },
};

const actions = {
  async initialize({ commit, dispatch }) {
    commit('resetTable');
    await dispatch('fetchScs');
    await dispatch('initSearchParams');
  },
  resetTable({ commit }) {
    commit('resetTable');
  },
  async loadTableData({ dispatch, commit }) {
    commit('resetTable');
    await dispatch('loadCertificateAccountData');
  },
  async fetchScs({ commit }) {
    try {
      const { data: { entities } } = await IDENTITY_API.get('entities');
      commit('setScs', entities.filter((x) => x.type === 'COMPANY'));
    } catch (error) {
      this.$notify('Failed to fetch SCs', 'error');
      console.error(error);
    }
  },
  async loadCertificateAccountData({ state, commit, dispatch }) {
    const tz = dateStore.getTimeZone();
    const momentStartDate = dateStore.toDateFromLocal(state.selectedDates[0], tz);
    const momentEndDate = dateStore.toDateFromLocal(state.selectedDates[1], tz);
    await Promise.all(state.selectedEntityNames.map(async (entityName) => {
      const params = {
        systemName: state.selectedSystemNames,
        entityName,
        status: state.selectedStatus,
        startDate: momentStartDate.toISOString(),
        endDate: momentEndDate.toISOString(),
      };
      await RECS_MANAGEMENT_API.get('/recs-management/account/accounts', { params }).then(({ data }) => {
        commit('addAccountData', data);
      }).catch((err) => {
        this.$notify('Failed to Load Accounts', 'error');
      });
      await RECS_MANAGEMENT_API.get('/recs-management/certificate/certificateQuantities', { params }).then(({ data }) => {
        commit('addCertificateQtyData', data);
      }).catch((err) => {
        this.$notify('Failed to Load Certificate Qtys', 'error');
      });
      await RECS_MANAGEMENT_API.get('/recs-management/certificate/certificateEligibilities', { params }).then(({ data }) => {
        commit('addCertificateEligibilityData', data);
      }).catch((err) => {
        this.$notify('Failed to Load Eligibilities', 'error');
      });
    }));

    this.$notify(`${state.certificateQtyData.length} Certificate Qtys Loaded`, 'info');
    const mappedData = await dispatch('mapCertificateData');
    commit('setCertificateTableData', mappedData);
    if (!state.hideZeros) {
      commit('addEmptyAccounts');
    }
  },
  async getSavedSearchParams({ state, dispatch }) {
    const params = await dispatch('getRecsSearchParams', state.screenName);
    return params;
  },
  setDefaultSearchParams({ commit, state, getters }) {
    let params = {};
    const endDate = getters.defaultEndDate;
    const startDate = endDate.clone().subtract(state.defaultDateDiff, 'days');
    params.startDate = startDate;
    params.endDate = endDate;
    params.status = state.defaultStatus;
    commit('setSearchParams', params);
  },
  async initSearchParams({ commit, state, dispatch, getters }) {
    const savedParams = await dispatch('getSavedSearchParams');
    if(savedParams) {      
      let endDate = savedParams.endDate ?? getters.defaultEndDate;
      endDate = dateStore.toDateFromLocal(endDate);
      const dateDiff = savedParams.dateRangeDiff ?? state.defaultDateDiff;
      const startDate = endDate.clone().subtract(dateDiff, 'days');

      const params = {
        systemName: savedParams.systemName,
        entityNames: savedParams.entityNames,
        status: savedParams.status ?? state.defaultStatus,
        endDate,
        startDate,
      };

      commit('setSearchParams', params);
    } else {
      dispatch('setDefaultSearchParams');
    }
  },
  async saveSearchParams({ state, dispatch, getters }) {
    const searchParams = getters.currentSearchParams;
    const savePayload =  { 
      resource: state.screenName, 
      payload: searchParams 
    };
    await dispatch('saveRecsSearchParams', savePayload);
    return searchParams;
  },
  mapCertificateData({ state }) {
    const certificateData = state.certificateQtyData.map((x) => {
      // transaction data is not needed for this screen
      delete x.transactionDetail;
      return x;
    }).reduce((acc, item) => {
      // maps to a structure of [{ certificate: { ...certificateProps }, qtys: [{ ...qtyProps }] }]
      const { ...certQty } = item;
      const { certificate } = certQty;
      const elig = state.certificateEligibilityData.find((x) => x.certificate.id === certificate.id);
      certificate.eligibility = elig ? elig.eligibilityShortNames.join(',') : null;
      delete certQty.certificate;
      const existingItem = acc.find((x) => x.certificate.id === certificate.id);
      if (existingItem) {
        existingItem.qtys.push(certQty);
      } else {
        acc.push({ certificate, qtys: [certQty] });
      }
      return acc;
    }, []);

    const certQtyData = certificateData.reduce((acc, item) => {
      // flattens certificateData into a list of certificates and their qtys to match table config
      const { certificate, qtys } = item;
      const certificateRow = qtys.map((qty) => ({ ...certificate, ...qty }));
      acc.push(...certificateRow);
      return acc;
    }, []);

    return certQtyData;
  },
  ...RecsBaseStore.actions,
};

const mutations = {
  resetTable(state) {
    state.certificateTableData = [];
    state.certificateQtyData = [];
    state.certificateEligibilityData = [];
    state.accountData = [];
    state.selectedRows = [];
    state.numSelectedRows = 0;
    state.numSelectedRecs = 0;
  },
  setSelectedSystems(state, value) {
    state.selectedSystemNames = value;
  },
  setSelectedEntities(state, value) {
    state.selectedEntityNames = value;
  },
  setSelectedStatus(state, value) {
    state.selectedStatus = value;
  },
  setSelectedDates(state, value) {
    state.selectedDates = value;
  },
  setCertificateTableData(state, data) {
    state.certificateTableData = data;
  },
  addEmptyAccounts(state) {
    const usedAccounts = state.certificateTableData.map((x) => x.account);
    const unusedAccounts = state.accountData.filter((x) => !usedAccounts.some(y => y.shortId === x.shortId));
    // need to set quantity to 0 for selected RECs sum calculation
    state.certificateTableData = state.certificateTableData.concat(unusedAccounts.map((x) => ({ account: x, quantity: 0 })));
  },
  removeEmptyAccounts(state) {
    // we set quantity to 0 for empty accounts, and only keep the id of a certificateQuantity so we can filter these out
    state.certificateTableData = state.certificateTableData.filter((x) => x.quantity > 0 && x.hasOwnProperty('id'));
  },
  addCertificateEligibilityData(state, certificateEligibilities) {
    state.certificateEligibilityData = state.certificateEligibilityData.concat(certificateEligibilities);
  },
  addCertificateQtyData(state, certificateQtys) {
    state.certificateQtyData = state.certificateQtyData.concat(certificateQtys);
  },
  addAccountData(state, accountData) {
    state.accountData = state.accountData.concat(accountData);
  },
  setSelectedRows(state, value) {
    state.selectedRows = value;
    state.numSelectedRows = value.length;
    const currentSelectedRows = value.length > 0 ? value : state.certificateTableData;
    state.numSelectedRecs = currentSelectedRows.reduce((acc, item) => acc + item.quantity, 0);
  },
  setSearchParams(state, value) {
    state.selectedSystemNames = value.systemName;
    state.selectedEntityNames = value.entityNames;
    state.selectedStatus = value.status;
    state.selectedDates = [value.startDate, value.endDate];
  },
  setHideZeros(state, value) {
    state.hideZeros = value;
  },
  ...createMutations(
    'scs',
    'numSelectedRows',
    'numSelectedRecs',
  ),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};